import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { LogOut, ChevronDown, User, Settings } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { signOutRequest } from "../../redux/auth/auth.actions";
import { PROFILE_SETTINGS } from "../../routes/routes";

const Bar = styled.div`
  flex-shrink: 0;
  background: #ededed;
  border-bottom: 1px solid #d3d3d3;
  padding: 0.75rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const Title = styled.h1`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const UserSection = styled.div`
  position: relative;
`;

const ExpandableInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-left: 0.5rem;
`;

const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1.5px solid rgba(17, 24, 39, 0.16);
  color: #111827;
  flex-shrink: 0;
`;

const TriggerButton = styled.button`
  display: flex;
  align-items: center;
  padding: 0.25rem 0.75rem 0.25rem 0.25rem;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 8px;
  background: transparent;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03), inset 0 1px 0 rgba(255, 255, 255, 0.6);
  cursor: pointer;
  outline: none;

  &:hover {
    border-color: rgba(17, 24, 39, 0.16);
    background: rgba(17, 24, 39, 0.03);
  }

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

const TriggerName = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
`;

const ChevronIcon = styled(ChevronDown)`
  transition: transform 0.2s ease;
  transform: rotate(${(props) => (props.$isOpen ? "180deg" : "0deg")});
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  width: 220px;
  background: white;
  border: 1px solid rgba(17, 24, 39, 0.08);
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05), 0 10px 24px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  overflow: hidden;
  z-index: 20;
`;

const DropdownUserInfo = styled.div`
  padding: 0.875rem 1rem;
  border-bottom: 1px solid rgba(17, 24, 39, 0.08);
`;

const UserName = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserEmail = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 0.125rem;
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  color: #374151;
  cursor: pointer;
  font-size: 0.875rem;

  &:hover {
    background: #f3f4f6;
  }

  &:focus {
    outline: none;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ProfileSettingsButton = styled(LogoutButton)`
  border-radius: 0;
  border-bottom: 1px solid #e5e7eb;

  &:hover {
    border-color: #e5e7eb;
  }
`;

function PageTopBar({ title }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminData = useSelector((state) => state.auth?.admin_data);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    dispatch(signOutRequest({ func: () => {} }));
  };

  const handleProfileSettings = () => {
    setIsMenuOpen(false);
    navigate(PROFILE_SETTINGS);
  };

  const username = adminData?.username || "User";

  return (
    <Bar>
      <Title>{title}</Title>

      <UserSection ref={menuRef}>
        <TriggerButton onClick={() => setIsMenuOpen((open) => !open)}>
          <Avatar>
            <User size={14} />
          </Avatar>
          <ExpandableInfo>
            <TriggerName>{username}</TriggerName>
            <ChevronIcon size={16} color="#111827" $isOpen={isMenuOpen} />
          </ExpandableInfo>
        </TriggerButton>

        {isMenuOpen && (
          <DropdownMenu>
            <DropdownUserInfo>
              <UserName>{username}</UserName>
              <UserEmail>{adminData?.email || ""}</UserEmail>
            </DropdownUserInfo>
            <ProfileSettingsButton onClick={handleProfileSettings}>
              <Settings size={16} />
              Profile Settings
            </ProfileSettingsButton>
            <LogoutButton onClick={handleLogout}>
              <LogOut size={16} />
              Logout
            </LogoutButton>
          </DropdownMenu>
        )}
      </UserSection>
    </Bar>
  );
}

export default PageTopBar;
